// src/pages/patient/MyOrders.js
import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, Package, Truck, CheckCircle, XCircle, 
  Clock, MapPin, Phone, Star, Eye, 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import patientService from '../../services/patientService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import Badge from '../../components/ui/Badge';

const MyOrders = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  useEffect(() => {
    loadOrders();
  }, [filters, pagination.page]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        patientId: user.id,
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status || undefined,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await patientService.getOrders(params);
      
      setOrders(response.data?.data?.orders || []);
      setPagination(prev => ({
        ...prev,
        total: response.data?.data?.pagination?.total || 0
      }));

    } catch (error) {
      console.error('Load orders error:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (order) => {
    try {
      setLoading(true);
      const response = await patientService.getOrderById(order._id);
      setSelectedOrder(response.data?.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Load order details error:', error);
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await patientService.cancelOrder(orderId, 'Cancelled by patient');
      
      setSuccess('Order cancelled successfully');
      loadOrders();
      setShowDetailsModal(false);

    } catch (error) {
      console.error('Cancel order error:', error);
      setError(error.response?.data?.message || 'Failed to cancel order.');
    } finally {
      setLoading(false);
    }
  };

  const handleRateOrder = async (orderId, rating, comment) => {
    try {
      setLoading(true);
      setError('');

      await patientService.rateOrder(orderId, { rating, comment });
      
      setSuccess('Thank you for your feedback!');
      loadOrders();

    } catch (error) {
      console.error('Rate order error:', error);
      setError('Failed to submit rating.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        label: 'Pending'
      },
      confirmed: {
        color: 'bg-blue-100 text-blue-800',
        icon: CheckCircle,
        label: 'Confirmed'
      },
      processing: {
        color: 'bg-purple-100 text-purple-800',
        icon: Package,
        label: 'Processing'
      },
      packed: {
        color: 'bg-indigo-100 text-indigo-800',
        icon: Package,
        label: 'Packed'
      },
      shipped: {
        color: 'bg-blue-100 text-blue-800',
        icon: Truck,
        label: 'Shipped'
      },
      out_for_delivery: {
        color: 'bg-cyan-100 text-cyan-800',
        icon: Truck,
        label: 'Out for Delivery'
      },
      delivered: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        label: 'Delivered'
      },
      cancelled: {
        color: 'bg-red-100 text-red-800',
        icon: XCircle,
        label: 'Cancelled'
      },
      returned: {
        color: 'bg-gray-100 text-gray-800',
        icon: XCircle,
        label: 'Returned'
      }
    };

    return statuses[status] || statuses.pending;
  };

  const OrderCard = ({ order }) => {
    const statusInfo = getStatusInfo(order.status);
    const StatusIcon = statusInfo.icon;

    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-900">
              Order #{order.orderNumber}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {new Date(order.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <Badge className={statusInfo.color}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusInfo.label}
          </Badge>
        </div>

        {/* Pharmacy */}
        <div className="mb-4 pb-4 border-b">
          <div className="flex items-center">
            <ShoppingBag className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-900">
              {order.pharmacy?.name || 'Pharmacy'}
            </span>
          </div>
          {order.pharmacy?.phone && (
            <div className="flex items-center mt-2">
              <Phone className="w-3 h-3 text-gray-400 mr-2" />
              <span className="text-xs text-gray-600">{order.pharmacy.phone}</span>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {order.items?.length || 0} item(s) • KES {order.totalAmount?.toLocaleString() || 0}
          </p>
          {order.items?.slice(0, 2).map((item, index) => (
            <p key={index} className="text-xs text-gray-500 mt-1">
              • {item.medicine?.name || 'Medicine'} × {item.quantity}
            </p>
          ))}
          {order.items?.length > 2 && (
            <p className="text-xs text-gray-500 mt-1">
              +{order.items.length - 2} more item(s)
            </p>
          )}
        </div>

        {/* Delivery Info */}
        {order.deliveryMethod === 'delivery' && order.deliveryAddress && (
          <div className="mb-4 pb-4 border-b">
            <div className="flex items-start">
              <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
              <div>
                <p className="text-xs text-gray-600">Delivery Address:</p>
                <p className="text-xs text-gray-900 mt-0.5">
                  {order.deliveryAddress.street}, {order.deliveryAddress.area}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tracking */}
        {order.trackingNumber && (
          <div className="mb-4">
            <p className="text-xs text-gray-600">
              Tracking: <span className="font-mono text-gray-900">{order.trackingNumber}</span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewDetails(order)}
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </Button>

          {order.status === 'pending' && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => handleCancelOrder(order._id)}
            >
              Cancel Order
            </Button>
          )}

          {order.status === 'delivered' && !order.rating && (
            <Button
              size="sm"
              onClick={() => {
                setSelectedOrder(order);
                // Open rating modal
              }}
            >
              <Star className="w-3 h-3 mr-1" />
              Rate Order
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and manage your medicine orders
          </p>
        </div>
      </div>

      {/* Success Alert */}
      {success && (
        <Alert type="success" title="Success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert type="error" title="Error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            placeholder="Search orders..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />

          <Select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </Select>

          <Select
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
          >
            <option value="createdAt">Order Date</option>
            <option value="totalAmount">Total Amount</option>
            <option value="status">Status</option>
          </Select>

          <Select
            value={filters.sortOrder}
            onChange={(e) => setFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </Select>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : orders.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {orders.map(order => (
              <OrderCard key={order._id} order={order} />
            ))}
          </div>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border p-4">
              <p className="text-sm text-gray-600">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} orders
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border">
          <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No orders found</p>
          <p className="text-sm text-gray-400">
            Your medicine orders will appear here once you place them
          </p>
        </div>
      )}

      {/* Order Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Order Details"
        size="xl"
      >
        {selectedOrder && (
          <div className="space-y-6">
            {/* Order Header */}
            <div className="flex items-center justify-between pb-4 border-b">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  Order #{selectedOrder.orderNumber}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Placed on {new Date(selectedOrder.createdAt).toLocaleDateString()}
                </p>
              </div>
              <Badge className={getStatusInfo(selectedOrder.status).color}>
                {getStatusInfo(selectedOrder.status).label}
              </Badge>
            </div>

            {/* Items */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Order Items</h3>
              <div className="space-y-3">
                {selectedOrder.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.medicine?.name}</p>
                      <p className="text-sm text-gray-600">{item.medicine?.manufacturer}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Quantity: {item.quantity} × KES {item.price?.toLocaleString()}
                      </p>
                    </div>
                    <p className="font-semibold text-gray-900">
                      KES {(item.price * item.quantity)?.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="pt-4 border-t">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">
                    KES {selectedOrder.subtotal?.toLocaleString()}
                  </span>
                </div>
                {selectedOrder.deliveryFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery Fee:</span>
                    <span className="font-medium">
                      KES {selectedOrder.deliveryFee?.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium">
                    KES {selectedOrder.tax?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span>KES {selectedOrder.totalAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            {selectedOrder.deliveryMethod === 'delivery' && selectedOrder.deliveryAddress && (
              <div className="pt-4 border-t">
                <h3 className="font-medium text-gray-900 mb-3">Delivery Information</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-900">
                    {selectedOrder.deliveryAddress.contactPerson}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedOrder.deliveryAddress.street}, {selectedOrder.deliveryAddress.area}
                    <br />
                    {selectedOrder.deliveryAddress.city}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Phone: {selectedOrder.deliveryAddress.contactPhone}
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </Button>
              {selectedOrder.status === 'pending' && (
                <Button
                  variant="danger"
                  onClick={() => handleCancelOrder(selectedOrder._id)}
                >
                  Cancel Order
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyOrders;